import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import type { NoteCategory } from "../types.ts";

interface NoteEntry {
  object: CSS2DObject;
  dot: THREE.Points;
  group: THREE.Group;
}

const CATEGORY_COLORS: Record<NoteCategory, number> = {
  room: 0x4a9eff,
  note: 0xffffff,
  damage: 0xff4a4a,
};

const SVG_ICONS: Record<NoteCategory, string> = {
  room: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  note: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  damage: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
};

export class NoteAnnotationManager {
  private scene: THREE.Scene;
  private entries: Map<string, NoteEntry> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  addNote(
    id: string,
    category: NoteCategory,
    point: THREE.Vector3,
    text: string,
    parent?: THREE.Object3D,
  ): void {
    this.remove(id);

    const group = new THREE.Group();
    group.name = `note_${id}`;
    // Place the entire group at the hit point — children stay at local origin
    group.position.copy(point);

    // CSS2D label with icon + text
    const div = document.createElement("div");
    div.className = `note-label note-label--${category}`;
    div.dataset.noteId = id;
    div.innerHTML = `
      <span class="note-icon">${SVG_ICONS[category]}</span>
      <span class="note-text">${_escHtml(text)}</span>
    `;
    const labelObj = new CSS2DObject(div);
    // label sits at group origin = at the hit point
    group.add(labelObj);

    // Dot marker at group origin
    const color = CATEGORY_COLORS[category];
    const dotGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3()]);
    const dotMat = new THREE.PointsMaterial({
      color,
      size: 0.05,
      depthTest: false,
      sizeAttenuation: true,
    });
    const dot = new THREE.Points(dotGeo, dotMat);
    dot.renderOrder = 999;
    group.add(dot);

    (parent ?? this.scene).add(group);
    this.entries.set(id, { object: labelObj, dot, group });
  }

  remove(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.group.traverse((obj) => {
      if (obj instanceof CSS2DObject) {
        obj.element.remove();
      }
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
        else (obj.material as THREE.Material).dispose();
      }
    });
    entry.group.removeFromParent();
    this.entries.delete(id);
  }

  /** Briefly highlight a note annotation (pulse + outline). */
  highlight(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;

    const div = entry.object.element;
    div.classList.add("note-label--highlight");
    setTimeout(() => div.classList.remove("note-label--highlight"), 2000);

    // Pulse the dot size
    const mat = entry.dot.material as THREE.PointsMaterial;
    const origSize = mat.size;
    mat.size = 0.2;
    setTimeout(() => { mat.size = origSize; }, 2000);
  }

  /** Get the world position of a note (for camera focus). */
  getWorldPosition(id: string): THREE.Vector3 | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    return entry.group.position.clone();
  }

  setVisible(visible: boolean): void {
    for (const entry of this.entries.values()) {
      entry.group.visible = visible;
    }
  }

  clear(): void {
    for (const id of this.entries.keys()) this.remove(id);
  }

  dispose(): void {
    this.clear();
  }
}

function _escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
